import { lazy, Suspense, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Header from "@/components/Header";
import HeroSection from "@/components/HeroSection";
import { useSeo } from "@/hooks/useSeo";

import FAQSection from "@/components/FAQSection";
import Footer from "@/components/Footer";

const AboutSection = lazy(() => import("@/components/AboutSection"));
const ImageSlider = lazy(() => import("@/components/ImageSlider"));
const WhyChooseUs = lazy(() => import("@/components/WhyChooseUs"));
const TawkToChat = lazy(() => import("@/components/TawkToChat"));

const SectionFallback = ({ className = "" }: { className?: string }) => (
  <div className={`py-16 md:py-24 ${className}`} />
);

const Index = () => {
  const location = useLocation();

  useSeo({
    title: "Cheap Southampton Port Parking | Cruise Park & Ride",
    description: "Book cheap Southampton Port parking with secure Park and Stroll and Park and Ride services. Fast terminal access and affordable cruise parking.",
    keywords: "Southampton Port parking, Southampton cruise parking, Park and Stroll Southampton, Park and Ride Southampton, cheap cruise parking UK, Southampton cruise terminal parking",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        serviceType: "Cruise Port Parking",
        provider: {
          "@type": "LocalBusiness",
          name: "Southampton Port Parking",
          url: "https://southamptonportparkingservices.co.uk/",
        },
        areaServed: { "@type": "Place", name: "Southampton Port, UK" },
        offers: [
          { "@type": "Offer", name: "Park and Stroll", priceCurrency: "GBP", url: "https://southamptonportparkingservices.co.uk/services" },
          { "@type": "Offer", name: "Park and Ride", priceCurrency: "GBP", url: "https://southamptonportparkingservices.co.uk/services" },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://southamptonportparkingservices.co.uk/" },
        ],
      },
    ],
  });

  useEffect(() => {
    if (location.hash) {
      const sectionId = location.hash.replace("#", "");
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    }
  }, [location.hash]);

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        <HeroSection />
        <Suspense fallback={<SectionFallback className="bg-card" />}>
          <ImageSlider />
        </Suspense>
        <Suspense fallback={<SectionFallback className="bg-cream" />}>
          <AboutSection />
        </Suspense>
        <Suspense fallback={<SectionFallback className="bg-card" />}>
          <WhyChooseUs />
        </Suspense>
        <FAQSection />
      </main>
      <Footer />
      <Suspense fallback={null}>
        <TawkToChat />
      </Suspense>
    </div>
  );
};

export default Index;
